import {
  calendarDateFromParts,
  formatCalendarMonth,
  formatCalendarMonthAndYear,
  getTypedLanguage,
  isAfter,
  isBefore,
  isSameMonthAndYear,
  startOfCalendarMonth,
  today,
  type CalendarDate,
} from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';
import { useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { Div } from '@ui/components/base/div';
import { Field, FieldLabel, FieldVariant, getFieldLabelVariant } from '@ui/components/base/field';
import { InputGroupAddon, InputGroupButton, InputGroupField, InputGroupInput } from '@ui/components/base/input-group';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/base/popover';
import { Typography } from '@ui/components/base/typography';
import { FieldErrorIndicator } from '@ui/components/fields/fieldErrorIndicator';
import { ArrowLeftIcon, ArrowRightIcon, CalendarIcon, XIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

export interface MonthPickerProps {
  value: CalendarDate | null;
  onChange: (date: CalendarDate | null) => void;
  label?: string;
  variant?: FieldVariant;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  minDate?: CalendarDate;
  maxDate?: CalendarDate;
  error?: boolean;
  helperText?: string;
  clearable?: boolean;
  id?: string;
  className?: string;
  'data-test'?: string;
}

const MONTHS_IN_YEAR = 12;
const FIRST_DAY_OF_MONTH = 1;
const MONTH_NUMBERS: number[] = Array.from({ length: MONTHS_IN_YEAR }, (_, index) => index + 1);

const isMonthOutOfRange = (monthStart: CalendarDate, minDate?: CalendarDate, maxDate?: CalendarDate): boolean =>
  (!isNil(minDate) && isBefore(monthStart, startOfCalendarMonth(minDate))) ||
  (!isNil(maxDate) && isAfter(monthStart, startOfCalendarMonth(maxDate)));

const getInitialYear = (value: CalendarDate | null, minDate?: CalendarDate, maxDate?: CalendarDate): number => {
  if (!isNil(value)) {
    return value.year;
  }
  const currentYear = today().year;
  if (!isNil(minDate) && currentYear < minDate.year) {
    return minDate.year;
  }
  if (!isNil(maxDate) && currentYear > maxDate.year) {
    return maxDate.year;
  }
  return currentYear;
};

const MonthPicker = ({
  value,
  onChange,
  label,
  variant = FieldVariant.DEFAULT,
  placeholder,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  error = false,
  helperText,
  clearable = false,
  id,
  className,
  'data-test': dataTest,
}: MonthPickerProps) => {
  const { locale } = useIntl();
  const generatedId = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [displayedYear, setDisplayedYear] = useState<number>(() => getInitialYear(value, minDate, maxDate));

  const language = getTypedLanguage(locale);
  const inputId = id ?? generatedId;
  const hasRangeError = !isNil(value) && isMonthOutOfRange(startOfCalendarMonth(value), minDate, maxDate);
  const hasError = error || hasRangeError;
  const displayValue = isNil(value) ? '' : formatCalendarMonthAndYear(value, language, 'long');
  const canGoToPreviousYear = isNil(minDate) || displayedYear > minDate.year;
  const canGoToNextYear = isNil(maxDate) || displayedYear < maxDate.year;
  const isClearVisible = clearable && !isNil(value) && !disabled;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDisplayedYear(getInitialYear(value, minDate, maxDate));
    }
    setOpen(nextOpen);
  };

  const handleSelect = (monthStart: CalendarDate) => {
    onChange(monthStart);
    setOpen(false);
  };

  return (
    <Field className={className} data-test={dataTest}>
      {label && (
        <FieldLabel
          htmlFor={inputId}
          variant={getFieldLabelVariant(variant)}
          className={cn(required && "after:ml-0.5 after:text-destructive after:content-['*']")}
        >
          {label}
        </FieldLabel>
      )}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <InputGroupField
          ref={fieldRef}
          className={cn('h-12 rounded-lg', disabled && 'pointer-events-none opacity-50')}
          data-disabled={disabled || undefined}
        >
          <PopoverTrigger
            id={inputId}
            nativeButton={false}
            disabled={disabled}
            render={
              <InputGroupInput
                role="combobox"
                readOnly
                value={displayValue}
                placeholder={placeholder}
                disabled={disabled}
                aria-invalid={hasError || undefined}
                aria-required={required || undefined}
                className="h-full cursor-pointer px-3 font-normal capitalize"
              />
            }
          />
          <InputGroupAddon align="inline-end">
            {hasError && !isNil(helperText) && <FieldErrorIndicator messageId={helperText} />}
            {isClearVisible && (
              <InputGroupButton
                variant={ButtonVariant.GHOST}
                size="icon-xs"
                onClick={() => onChange(null)}
                aria-label="Clear month"
                className="text-muted-foreground"
              >
                <XIcon className="size-4" />
              </InputGroupButton>
            )}
            <PopoverTrigger
              render={<InputGroupButton variant={ButtonVariant.GHOST} size="icon-xs" tabIndex={-1} />}
              disabled={disabled}
              aria-label="Open month picker"
              className="cursor-pointer hover:bg-transparent focus-visible:ring-0 aria-expanded:bg-transparent"
            >
              <CalendarIcon className="size-4" />
            </PopoverTrigger>
          </InputGroupAddon>
        </InputGroupField>
        <PopoverContent anchor={fieldRef} align="start" className="w-72 gap-3 p-3">
          <Div row className="items-center justify-between">
            <Button
              variant={ButtonVariant.GHOST}
              size="icon-sm"
              aria-label="Previous year"
              disabled={!canGoToPreviousYear}
              onClick={() => setDisplayedYear(displayedYear - 1)}
            >
              <ArrowLeftIcon className="size-4" />
            </Button>
            <Typography className="font-medium" aria-live="polite">
              {displayedYear}
            </Typography>
            <Button
              variant={ButtonVariant.GHOST}
              size="icon-sm"
              aria-label="Next year"
              disabled={!canGoToNextYear}
              onClick={() => setDisplayedYear(displayedYear + 1)}
            >
              <ArrowRightIcon className="size-4" />
            </Button>
          </Div>
          <Div grid className="grid-cols-3 gap-1">
            {MONTH_NUMBERS.map((month) => {
              const monthStart = calendarDateFromParts(displayedYear, month, FIRST_DAY_OF_MONTH);
              const isSelected = !isNil(value) && isSameMonthAndYear(value, monthStart);
              return (
                <Button
                  key={month}
                  variant={isSelected ? ButtonVariant.DEFAULT : ButtonVariant.GHOST}
                  shape="rounded"
                  aria-pressed={isSelected}
                  disabled={isMonthOutOfRange(monthStart, minDate, maxDate)}
                  onClick={() => handleSelect(monthStart)}
                  className="capitalize"
                >
                  {formatCalendarMonth(monthStart, language)}
                </Button>
              );
            })}
          </Div>
        </PopoverContent>
      </Popover>
    </Field>
  );
};

export { MonthPicker };
